import { useState, useCallback } from 'react';
import {
  Sparkles, Loader2, CheckCircle, AlertTriangle, ChevronDown, ChevronUp,
  RefreshCw, ClipboardList, MessageSquare, Copy
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

// ─── Copy helper ──────────────────────────────────────────────────────────────
function formatQuestionsForClipboard(questions, categories, domainMandatory) {
  const lines = ['=== AI SCREENING QUESTIONS ===\n'];
  for (const cat of categories) {
    const qs = questions[cat.key] || [];
    if (!qs.length) continue;
    if (cat.domainOnly && !domainMandatory) continue;
    lines.push(`--- ${cat.label.toUpperCase()} (${cat.qRange}) ---`);
    qs.forEach(q => {
      lines.push(`${q.id.toUpperCase()}. ${q.text}`);
      if (q.probes_for) lines.push(`   Looks for: ${q.probes_for}`);
      lines.push('');
    });
  }
  return lines.join('\n').trim();
}

// ─── Category config ──────────────────────────────────────────────────────────
const CATEGORIES = [
  {
    key: 'technical', label: 'Technical Skills',
    qRange: 'q1–q6',
    bg: 'bg-blue-50', border: 'border-blue-100',
    text: 'text-blue-700', dot: 'bg-blue-500', bar: '#2563eb',
  },
  {
    key: 'projects', label: 'Experience Depth',
    qRange: 'q7–q9',
    bg: 'bg-green-50', border: 'border-green-100',
    text: 'text-green-700', dot: 'bg-green-500', bar: '#16a34a',
  },
  {
    key: 'domain', label: 'Domain / Environment',
    qRange: 'q10–q11',
    bg: 'bg-purple-50', border: 'border-purple-100',
    text: 'text-purple-700', dot: 'bg-purple-500', bar: '#7c3aed',
    domainOnly: true,
  },
  {
    key: 'recency', label: 'Seniority / Trajectory',
    qRange: 'q12',
    bg: 'bg-amber-50', border: 'border-amber-100',
    text: 'text-amber-700', dot: 'bg-amber-500', bar: '#d97706',
  },
  {
    key: 'behavioral', label: 'Behavioral / Flags',
    qRange: 'q13–q15',
    bg: 'bg-red-50', border: 'border-red-100',
    text: 'text-red-700', dot: 'bg-red-500', bar: '#dc2626',
  },
];

function scoreConfig(s) {
  if (s >= 70) return { label: 'Strong Match', bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' };
  if (s >= 50) return { label: 'Good Match',   bg: 'bg-blue-50',  text: 'text-blue-700',  border: 'border-blue-200'  };
  if (s >= 30) return { label: 'Review',        bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' };
  return              { label: 'Poor Fit',      bg: 'bg-red-50',   text: 'text-red-700',   border: 'border-red-200'   };
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function CategorySection({ cat, questions, domainMandatory }) {
  const [open, setOpen] = useState(true);
  if (!questions?.length) return null;
  if (cat.domainOnly && !domainMandatory) return null;

  return (
    <div className={`rounded-xl border ${cat.border} ${cat.bg} overflow-hidden mb-3`}
         data-testid={`vq-category-${cat.key}`}>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3"
      >
        <div className="flex items-center gap-2.5">
          <span className={`w-2 h-2 rounded-full ${cat.dot}`} />
          <span className={`text-sm font-semibold ${cat.text}`}>{cat.label}</span>
          <span className={`text-xs px-2 py-0.5 rounded-full bg-white/70 ${cat.text} font-medium`}>
            {questions.length}Q
          </span>
        </div>
        {open ? <ChevronUp className={`w-4 h-4 ${cat.text}`} />
               : <ChevronDown className={`w-4 h-4 ${cat.text}`} />}
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-3">
          {questions.map((q, i) => (
            <div key={q.id} className="bg-white/80 rounded-lg p-3.5 border border-white"
                 data-testid={`vq-question-${q.id}`}>
              <div className="flex gap-2.5">
                <span className={`text-xs font-bold ${cat.text} mt-0.5 shrink-0`}>{q.id.toUpperCase()}</span>
                <div>
                  <p className="text-sm text-slate-800 leading-relaxed">{q.text}</p>
                  {q.probes_for && (
                    <p className="text-xs text-slate-500 mt-1.5 italic">
                      Looks for: {q.probes_for}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ScoreBar({ label, value, max, barColor }) {
  if (!max || max === 0) return null;
  const pct = Math.round(Math.min((value / max) * 100, 100));
  return (
    <div className="flex items-center gap-3 mb-2" data-testid={`vq-bar-${label.toLowerCase().replace(/\s/g,'-')}`}>
      <span className="text-xs text-slate-500 w-24 shrink-0">{label}</span>
      <div className="flex-1 bg-slate-100 rounded-full h-2">
        <div className="h-2 rounded-full transition-all duration-500"
             style={{ width: `${pct}%`, background: barColor }} />
      </div>
      <span className="text-xs font-semibold text-slate-700 w-10 text-right">{value}/{max}</span>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function VerificationQuestions({ candidateId, jobId, initialData, initialScore, initialBreakdown, initialFlags }) {
  const [vData, setVData]           = useState(initialData || null);
  const [generating, setGenerating] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [scoring, setScoring]       = useState(false);
  // Seed scoreResult from existing candidate-level data if the candidate is already screened
  const [scoreResult, setScoreResult] = useState(
    (initialData?.status === 'screened' && initialScore != null)
      ? { relevance_score: initialScore, score_breakdown: initialBreakdown || null, flags: initialFlags || null, scored_at: initialData.scored_at }
      : null
  );
  const [showTranscript, setShowTranscript] = useState(false);

  const questions      = vData?.questions || null;
  const claims         = vData?.claims_extracted || {};
  const domainMandatory = (claims.domain || []).length > 0;
  const isScreened     = vData?.status === 'screened';

  // If screened, show previously persisted breakdown from scoreResult or initialData
  const breakdown  = scoreResult?.score_breakdown  || null;
  const flags      = scoreResult?.flags            || null;
  const relScore   = scoreResult?.relevance_score  ?? null;
  const scoredAt   = scoreResult?.scored_at        || vData?.scored_at || null;
  const showOnDash = relScore != null ? relScore >= 50 : null;

  // ── Copy all questions ─────────────────────────────────────────────────────
  const handleCopy = useCallback(async () => {
    if (!questions) return;
    const text = formatQuestionsForClipboard(questions, CATEGORIES, domainMandatory);
    try {
      await navigator.clipboard.writeText(text);
      toast.success('15 questions copied to clipboard');
    } catch {
      // Fallback for browsers that block clipboard
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      toast.success('Questions copied!');
    }
  }, [questions, domainMandatory]);

  // ── Generate questions ─────────────────────────────────────────────────────
  const handleGenerate = async (force = false) => {
    if (!candidateId) return;
    setGenerating(true);
    try {
      const url = force
        ? `/candidates/${candidateId}/verification-questions?force_regenerate=true`
        : `/candidates/${candidateId}/verification-questions`;
      const { data } = await api.post(url);
      setVData(data.verification_data);
      toast.success(data.cached ? 'Questions loaded (cached)' : '15 questions generated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate questions');
    } finally {
      setGenerating(false);
    }
  };

  // ── Score transcript ───────────────────────────────────────────────────────
  const handleScore = async () => {
    if (transcript.trim().length < 100) return;
    setScoring(true);
    try {
      const { data } = await api.post(`/candidates/${candidateId}/score-transcript`, {
        transcript,
      });
      setScoreResult(data);
      // Sync status in local vData
      setVData(prev => prev ? { ...prev, status: 'screened', scored_at: data.scored_at } : prev);
      toast.success(`Scored! ${data.relevance_score}/100 — ${scoreConfig(data.relevance_score).label}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Scoring failed');
    } finally {
      setScoring(false);
    }
  };

  const handleRescore = () => {
    setTranscript('');
    setScoreResult(null);
    setShowTranscript(true);
  };

  // ── Empty state ────────────────────────────────────────────────────────────
  if (!questions && !generating) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm mb-6"
           data-testid="vq-panel">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2.5">
            <ClipboardList className="w-5 h-5 text-[#2563eb]" />
            <h2 className="text-base font-bold text-slate-800">AI Screening Questions</h2>
          </div>
        </div>
        <div className="text-center py-8 text-slate-400">
          <Sparkles className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          <p className="text-sm mb-4">Generate 15 targeted verification questions from the JD + profile</p>
          <button
            onClick={() => handleGenerate(false)}
            className="inline-flex items-center gap-2 bg-[#2563eb] hover:bg-[#1d4ed8] text-white px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors shadow-sm"
            data-testid="vq-generate-btn"
          >
            <Sparkles className="w-4 h-4" />
            Generate Questions
          </button>
        </div>
      </div>
    );
  }

  // ── Generating spinner ─────────────────────────────────────────────────────
  if (generating) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm mb-6"
           data-testid="vq-panel">
        <div className="flex items-center gap-3 py-6 justify-center text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin text-[#2563eb]" />
          <span className="text-sm">Extracting claims &amp; generating 15 questions…</span>
        </div>
      </div>
    );
  }

  const cfg = relScore != null ? scoreConfig(relScore) : null;

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm mb-6"
         data-testid="vq-panel">

      {/* ── SECTION 1: Header ──────────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
        <div className="flex items-center gap-2.5">
          <ClipboardList className="w-5 h-5 text-[#2563eb]" />
          <h2 className="text-base font-bold text-slate-800">AI Screening Questions</h2>
          {questions && (
            <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full font-medium">
              15 questions
            </span>
          )}
          {isScreened && (
            <span className="text-xs bg-green-50 text-green-700 border border-green-100 px-2 py-0.5 rounded-full font-medium">
              Screened
            </span>
          )}
        </div>
        <div className="flex gap-2">
          {questions && (
            <button
              onClick={handleCopy}
              className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#2563eb] px-3 py-1.5 rounded-lg hover:bg-blue-50 border border-slate-200 hover:border-blue-200 transition-colors"
              data-testid="vq-copy-btn"
              title="Copy all 15 questions to clipboard"
            >
              <Copy className="w-3.5 h-3.5" />
              Copy All
            </button>
          )}
          {!isScreened && questions && (
            <button
              onClick={() => handleGenerate(true)}
              className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 px-3 py-1.5 rounded-lg hover:bg-slate-50 border border-slate-200 transition-colors"
              data-testid="vq-regenerate-btn"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Regenerate
            </button>
          )}
          {!questions && !isScreened && (
            <button
              onClick={() => handleGenerate(false)}
              disabled={generating}
              className="flex items-center gap-2 bg-[#2563eb] hover:bg-[#1d4ed8] text-white px-4 py-2 rounded-xl text-sm font-semibold transition-colors"
              data-testid="vq-generate-btn"
            >
              <Sparkles className="w-4 h-4" />
              Generate Questions
            </button>
          )}
        </div>
      </div>

      <div className="p-6 space-y-6">

        {/* ── SECTION 2: Questions by category ─────────────────────────────── */}
        {questions && (
          <div data-testid="vq-questions-list">
            {CATEGORIES.map(cat => (
              <CategorySection
                key={cat.key}
                cat={cat}
                questions={questions[cat.key]}
                domainMandatory={domainMandatory}
              />
            ))}
            {claims.gaps?.length > 0 && (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mt-1">
                Skill gaps probed: {claims.gaps.join(' · ')}
              </div>
            )}
          </div>
        )}

        {/* ── SECTION 3: Transcript input ───────────────────────────────────── */}
        {questions && (
          <div data-testid="vq-transcript-section">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-slate-400" />
                <label className="text-sm font-semibold text-slate-700">
                  Paste Screening Transcript
                </label>
              </div>
              <span className="text-xs text-slate-400">{transcript.length} chars</span>
            </div>
            <textarea
              value={transcript}
              onChange={e => setTranscript(e.target.value)}
              placeholder="Recruiter: Walk me through...&#10;Candidate: Sure, I have 5 years of..."
              rows={8}
              className="w-full border border-slate-200 rounded-xl p-4 text-sm text-slate-700 placeholder-slate-300 focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/20 outline-none resize-y leading-relaxed"
              data-testid="vq-transcript-input"
            />
            <div className="flex items-center justify-between mt-3">
              <span className="text-xs text-slate-400">
                {transcript.length < 100
                  ? `${100 - transcript.length} more characters needed`
                  : 'Ready to score'}
              </span>
              <button
                onClick={handleScore}
                disabled={scoring || transcript.trim().length < 100}
                className="flex items-center gap-2 bg-[#2563eb] hover:bg-[#1d4ed8] disabled:bg-slate-200 disabled:text-slate-400 text-white px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors shadow-sm"
                data-testid="vq-score-btn"
              >
                {scoring
                  ? <><Loader2 className="w-4 h-4 animate-spin" />Scoring…</>
                  : <><Sparkles className="w-4 h-4" />Score Transcript</>}
              </button>
            </div>
          </div>
        )}

        {/* ── SECTION 4: Score result ───────────────────────────────────────── */}
        {cfg && breakdown && (
          <div className={`rounded-xl border ${cfg.border} ${cfg.bg} p-5`}
               data-testid="vq-score-result">

            {/* Big badge */}
            <div className="flex items-center gap-4 mb-5">
              <div className="text-center">
                <div className={`text-4xl font-bold ${cfg.text}`}>{relScore}</div>
                <div className="text-xs text-slate-500 mt-0.5">/ 100</div>
              </div>
              <div>
                <span className={`text-sm font-bold ${cfg.text}`}>{cfg.label}</span>
                <p className="text-xs text-slate-500 mt-0.5">Adaptive verification score</p>
              </div>
            </div>

            {/* Breakdown bars */}
            <div className="bg-white/70 rounded-xl p-4 mb-4">
              <ScoreBar label="Skills"       value={breakdown.skills}    max={breakdown.skills_max} barColor="#2563eb" />
              {breakdown.domain_max > 0 && (
                <ScoreBar label="Domain"     value={breakdown.domain}    max={breakdown.domain_max} barColor="#7c3aed" />
              )}
              <ScoreBar label="Exp Depth"    value={breakdown.exp_depth} max={10}                   barColor="#16a34a" />
              <ScoreBar label="Seniority"    value={breakdown.seniority} max={5}                    barColor="#d97706" />
              {breakdown.proctoring_deduct < 0 && (
                <div className="flex items-center gap-3 mt-2">
                  <span className="text-xs text-slate-500 w-24">Proctoring</span>
                  <span className="text-xs font-bold text-red-600 bg-red-50 border border-red-100 px-2 py-0.5 rounded-full"
                        data-testid="vq-proctoring-badge">
                    {breakdown.proctoring_deduct}pts
                  </span>
                </div>
              )}
            </div>

            {/* Flags */}
            {flags && (
              <div className="flex flex-wrap gap-2" data-testid="vq-flags">
                {flags.shape && (
                  <span className="text-xs px-2.5 py-1 bg-white/80 border border-slate-200 text-slate-600 rounded-full font-medium">
                    shape: {flags.shape}
                  </span>
                )}
                {flags.trajectory && (
                  <span className="text-xs px-2.5 py-1 bg-white/80 border border-slate-200 text-slate-600 rounded-full font-medium">
                    trajectory: {flags.trajectory}
                  </span>
                )}
                {flags.location_warning && (
                  <span className="text-xs px-2.5 py-1 bg-amber-50 border border-amber-100 text-amber-700 rounded-full font-medium flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    {flags.location_warning}
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── SECTION 5: Status footer ──────────────────────────────────────── */}
        {(isScreened || relScore != null) && (
          <div className="flex items-center justify-between pt-2 border-t border-slate-100"
               data-testid="vq-status-footer">
            <div className="flex items-center gap-3">
              {showOnDash === true && (
                <div className="flex items-center gap-1.5 text-xs text-green-700 bg-green-50 border border-green-100 px-3 py-1.5 rounded-lg font-medium"
                     data-testid="vq-dashboard-badge">
                  <CheckCircle className="w-3.5 h-3.5" />
                  Dashboard Visible
                </div>
              )}
              {showOnDash === false && (
                <div className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-100 px-3 py-1.5 rounded-lg font-medium"
                     data-testid="vq-dashboard-badge">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Below Dashboard Threshold
                </div>
              )}
              {scoredAt && (
                <span className="text-xs text-slate-400">
                  Scored {new Date(scoredAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
            </div>
            <button
              onClick={handleRescore}
              className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#2563eb] px-3 py-1.5 rounded-lg hover:bg-blue-50 border border-slate-200 hover:border-blue-200 transition-colors font-medium"
              data-testid="vq-rescore-btn"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Re-score
            </button>
          </div>
        )}

      </div>
    </div>
  );
}
